import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  Snackbar,
  Alert,
  styled,
  useTheme,
  Checkbox,
  FormControlLabel,
  Link,
} from "@mui/material";

import callBackFormSubmit from "../services/callBackFormSu";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "white",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#00cc87",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#00cc87",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#666",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#00cc87",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "8px",
  padding: "5px 30px",
  fontSize: "16px",
  marginTop: "20px",
  width: "150px",
  fontWeight: "bold",
  textTransform: "none",
  background: "#00cc87",
  boxShadow: "0 8px 20px rgba(211, 47, 47, 0.1)",
  "&:hover": {
    background: "#0265fe",
    transform: "translateY(-2px)",
  },
  transition: "all 0.3s ease",
}));

export default function CallBackForm({ callBackFormLabels, hideEmail = false }) {
  const [formData, setFormData] = useState({
    name: "",
    "email-address": "",
    "phone-number": "",
    "form-name": callBackFormLabels,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      // First API call (your existing one)
      await callBackFormSubmit(formData);

      // Second API call for email (new)
      const emailFormData = new FormData();

      // Map your form fields to the email API format
      emailFormData.append("smtpEmail", "psnfinanceblr@gmail.com");
      emailFormData.append("appPassword", "pnyl ysdr vfxk avmj");
      emailFormData.append("toEmail", "psnfinanceblr@gmail.com");
      emailFormData.append("subject", `New Callback Request`);

      // Create message content
      const message = `New callback request received.\n\nName: ${formData.name}\nEmail: ${formData["email-address"]}\nPhone: ${formData["phone-number"]}`;

      const html = `
      <h3>New Callback Request</h3>
      <p><strong>Name:</strong> ${formData.name}</p>
      <p><strong>Email:</strong> ${formData["email-address"]}</p>
      <p><strong>Phone:</strong> ${formData["phone-number"]}</p>
    `;

      emailFormData.append("message", message);
      emailFormData.append("html", html);

      // Send email
      const emailResponse = await fetch(
        "https://email-sender.backendprod.com/api/send-email",
        {
          method: "POST",
          headers: {
            "X-API-Key": "Dinesh@@@@",
          },
          body: emailFormData,
        }
      );

      const emailResult = await emailResponse.json();

      if (!emailResult.success) {
        console.error("Email sending failed:", emailResult.error);
        // You might want to handle this differently than throwing an error
      }

      // Show success message
      setSnackbar({
        open: true,
        message: "Callback request submitted successfully!",
        severity: "success",
      });

      // Reset form after successful submission
      setFormData({
        name: "",
        "email-address": "",
        "phone-number": "",
        "form-name": callBackFormLabels,
      });
    } catch (error) {
      console.error("Submission error:", error);
      setSnackbar({
        open: true,
        message: "Failed to submit request. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          // mt: 1,
          backgroundColor: "#f5f7fa",
          width: { xs: "100%", md: "80%" },
          borderRadius: "20px",
          p: { xs: 3, md: 5 },
        }}
      >
        <Stack spacing={2} sx={{ mb: { xs: 1, md: 2 } }}>
          <StyledTextField
            required
            fullWidth
            size={isMobile ? "small" : "medium"}
            id="name"
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            variant="outlined"
          />

          {!hideEmail && (
            <StyledTextField
              size={isMobile ? "small" : "medium"}
              required
              fullWidth
              id="email-address"
              label="Email"
              name="email-address"
              type="email"
              value={formData["email-address"]}
              onChange={handleChange}
              variant="outlined"
            />
          )}
        </Stack>

        <Stack spacing={2} alignItems="center" sx={{ mb: { xs: 1, md: 2 } }}>
          <StyledTextField
            required
            fullWidth
            size={isMobile ? "small" : "medium"}
            id="phone-number"
            label="Mobile No"
            name="phone-number"
            type="tel"
            value={formData["phone-number"]}
            onChange={handleChange}
            variant="outlined"
            inputProps={{ pattern: "[0-9]{10}" }}
          />
        </Stack>

        <Box sx={{ mt: 2, mb: 1 }}>
          <FormControlLabel
            control={<Checkbox required color="default" />}
            sx={{ '& .MuiFormControlLabel-asterisk': { display: 'none' } }}
            label={
              <Typography component="span" variant="body1" sx={{ display: 'inline', fontSize: { xs: "12px", md: "14px" }, textAlign: "left" }}>
                By continuing, you agree to provide your authorization to PSN Finance, PSN Group, and its affiliated entities to store and process your data as per the{" "}
                <Link component={RouterLink} to="/privacy-policy" underline="hover" color="primary">
                  Privacy Policy*
                </Link>
                .
              </Typography>
            }
          />
        </Box>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <StyledButton
              type="submit"
              variant="contained"
              id="apply-btn-register"
              size="small"
              disabled={isSubmitting}
              sx={{
                width: { xs: "50%", md: "150px" },
                fontSize: { xs: "14px", md: "16px" },
              }}
            >
              {isSubmitting ? "Submitting..." : "Apply Now"}
            </StyledButton>
          </Box>
        </Grid>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
